'use client';

import { useEffect, useId, useState } from 'react';
import { formatDate, toDate } from '@bridge/utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { twMerge } from 'tailwind-merge';
import { tv } from 'tailwind-variants';

import { faCalendar } from '@icons/classic/regular';
import { useIsMobile } from '../hooks';
import { Label } from '../typography/Label';
import { Button } from './Button';
import { Calendar } from './Calendar';
import { FlexContainer } from './FlexContainer';
import { Input, input } from './Input';
import { Popover, PopoverContent, PopoverTrigger } from './Popover';
import { ToggleGroup, ToggleGroupItem } from './ToggleGroup';

import type { DateRange, Matcher } from 'react-day-picker';

type DateRangePreset = {
  label: string;
  getRange: () => DateRange;
};

const DATE_RANGE_PRESETS = {
  ytd: {
    label: 'YTD',
    getRange: () => {
      const now = new Date();
      return {
        from: new Date(now.getFullYear(), 0, 1),
        to: now,
      };
    },
  },
  '1year': {
    label: '1 year',
    getRange: () => {
      const now = new Date();
      const oneYearAgo = new Date(now);
      oneYearAgo.setFullYear(now.getFullYear() - 1);
      return {
        from: oneYearAgo,
        to: now,
      };
    },
  },
  '6months': {
    label: '6 months',
    getRange: () => {
      const now = new Date();
      const sixMonthsAgo = new Date(now);
      sixMonthsAgo.setMonth(now.getMonth() - 6);
      return {
        from: sixMonthsAgo,
        to: now,
      };
    },
  },
  pastMonth: {
    label: 'Past month',
    getRange: () => {
      const now = new Date();
      const firstDayOfPrevMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const lastDayOfPrevMonth = new Date(now.getFullYear(), now.getMonth(), 0);
      return {
        from: firstDayOfPrevMonth,
        to: lastDayOfPrevMonth,
      };
    },
  },
  '3months': {
    label: '3 months',
    getRange: () => {
      const now = new Date();
      const threeMonthsAgo = new Date(now);
      threeMonthsAgo.setMonth(now.getMonth() - 3);
      return {
        from: threeMonthsAgo,
        to: now,
      };
    },
  },
  allTime: {
    label: 'All time',
    getRange: () => {
      return {
        from: new Date(2020, 0, 1),
        to: new Date(),
      };
    },
  },
} as const satisfies Record<string, DateRangePreset>;

export type PresetKey = keyof typeof DATE_RANGE_PRESETS;

const datePickerPlaceholder = tv({
  base: `
    text-content-secondary h-10
  `,
});

type SingleDateProps = {
  mode?: 'single';
  placeholder?: string;
  formatStr?: Intl.DateTimeFormatOptions;
  disabled?: boolean;
  onSelect?: (date: Date | undefined) => void;
  selected?: Date;
  /** The earliest selectable month. Defaults to 100 years ago. Accepts Date or ISO string. */
  startMonth?: Date | string;
  /** The latest selectable month. Defaults to 10 years from now. Accepts Date or ISO string. */
  endMonth?: Date | string;
  /** Days that render disabled and cannot be picked, e.g. `{ after: new Date() }` to rule out future days. */
  disabledDays?: Matcher | Matcher[];
};

type RangeDateProps = {
  mode: 'range';
  placeholder?: string;
  formatStr?: Intl.DateTimeFormatOptions;
  disabled?: boolean;
  onSelect?: (range: DateRange | undefined) => void;
  selected?: DateRange;
  /** Optional preset buttons for quick date range selection */
  presets?: PresetKey[];
  /**
   * Carries the range to the minute rather than the day: each end gets a time-of-day input under
   * the calendar, and the trigger reads the times back alongside the dates. The times ride on the
   * `Date`s already passed to `onSelect`, so nothing about the callback's shape changes.
   *
   * Time inputs read and write local time, so `formatStr` must use local-time formatting too.
   * Do not supply a `timeZone` such as `UTC`.
   *
   * Off by default. Left off, the calendar's midnight-anchored days are emitted untouched and the
   * trigger reads exactly as it did before this prop existed.
   */
  showTimeInput?: boolean;
};

type Props = SingleDateProps | RangeDateProps;

type DivProps = Omit<React.ComponentProps<'div'>, 'onSelect'>;

type DatePickerProps = DivProps & Props;

function isRangeProps(props: DatePickerProps): props is DivProps & RangeDateProps {
  return props.mode === 'range';
}

const DEFAULT_START_MONTH = new Date(new Date().getFullYear() - 100, 0, 1);
const DEFAULT_END_MONTH = new Date(new Date().getFullYear() + 10, 11, 31);

/** The time-of-day part of `date`, spelled the way `<input type="time">` reads and writes it. */
function toTimeValue(date: Date | undefined): string {
  if (!date) return '';

  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
}

/**
 * `date` moved to the time of day `timeValue` names, leaving its day alone. A value the input
 * cannot produce a time from — it is empty while being edited — leaves `date` as it was.
 */
function withTimeValue(date: Date, timeValue: string): Date {
  const [hours, minutes] = timeValue.split(':').map(Number);
  if (!Number.isInteger(hours) || !Number.isInteger(minutes)) return date;

  const next = new Date(date);
  next.setHours(hours, minutes, 0, 0);
  return next;
}

function withEndOfDayTime(date: Date): Date {
  return withTimeValue(date, '23:59');
}

function getRangeFormatWithTime(formatStr: Intl.DateTimeFormatOptions): Intl.DateTimeFormatOptions {
  if (formatStr.dateStyle || formatStr.timeStyle) {
    // Intl forbids style shortcuts together with individual component options such as hour/minute.
    return {
      ...formatStr,
      dateStyle: formatStr.dateStyle ?? 'short',
      timeStyle: formatStr.timeStyle ?? 'short',
    };
  }

  return { ...formatStr, hour: 'numeric', minute: '2-digit' };
}

function DatePicker(props: DatePickerProps): React.ReactElement {
  const {
    'aria-label': ariaLabel,
    className,
    placeholder = 'Pick a date',
    formatStr = { month: '2-digit', day: '2-digit', year: 'numeric' },
    disabled = false,
  } = props;

  const rangeProps = isRangeProps(props) ? props : null;
  const singleProps = isRangeProps(props) ? null : props;

  const presets = rangeProps?.presets;
  const showTimeInput = rangeProps?.showTimeInput ?? false;
  const startMonth = singleProps ? toDate(singleProps.startMonth ?? DEFAULT_START_MONTH) : undefined;
  const endMonth = singleProps ? toDate(singleProps.endMonth ?? DEFAULT_END_MONTH) : undefined;
  const [date, setDate] = useState<Date | undefined>(singleProps?.selected);
  const [dateRange, setDateRange] = useState<DateRange | undefined>(rangeProps?.selected);
  const [open, setOpen] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState<PresetKey | undefined>(undefined);
  const isMobile = useIsMobile();
  const timeInputId = useId();

  // Update internal state when selected prop changes
  useEffect(() => {
    if (props.mode === 'range') {
      setDateRange(props.selected);
    } else {
      setDate(props.selected);
    }
  }, [props.selected, props.mode]);

  const handleSelectSingle = (selectedDate: Date | undefined) => {
    setDate(selectedDate);
    singleProps?.onSelect?.(selectedDate);
    setOpen(false);
  };

  const emitRange = (nextRange: DateRange | undefined) => {
    setDateRange(nextRange);
    setSelectedPreset(undefined);
    rangeProps?.onSelect?.(nextRange);
  };

  const handleSelectRange = (selectedRange: DateRange | undefined) => {
    if (!showTimeInput || !selectedRange) {
      emitRange(selectedRange);
      return;
    }

    // The calendar hands back whole days, which would throw away the times already showing in the
    // inputs every time the operator moves a day. Carry them across instead.
    const fromTime = toTimeValue(dateRange?.from);
    const toTime = toTimeValue(dateRange?.to);
    emitRange({
      from: selectedRange.from && fromTime ? withTimeValue(selectedRange.from, fromTime) : selectedRange.from,
      to: selectedRange.to
        ? toTime
          ? withTimeValue(selectedRange.to, toTime)
          : withEndOfDayTime(selectedRange.to)
        : selectedRange.to,
    });
  };

  const handleTimeChange = (bound: 'from' | 'to', timeValue: string) => {
    const from = dateRange?.from;
    const to = dateRange?.to;
    const nextBound =
      bound === 'from' ? (from ? withTimeValue(from, timeValue) : from) : to ? withTimeValue(to, timeValue) : to;

    // An empty native time input is an intermediate edit. Keep the existing time in force and let
    // the controlled value restore it instead of emitting an unrelated range update.
    if (nextBound === (bound === 'from' ? from : to)) return;

    let nextFrom = bound === 'from' ? nextBound : from;
    let nextTo = bound === 'to' ? nextBound : to;

    // The calendar prevents inverted days, but the time controls can invert a range on one day.
    // Clamp the bound being edited to the other end so emitted ranges always remain valid.
    if (nextFrom && nextTo && nextFrom > nextTo) {
      if (bound === 'from') {
        nextFrom = new Date(nextTo);
      } else {
        nextTo = new Date(nextFrom);
      }
    }

    emitRange({ from: nextFrom, to: nextTo });
  };

  const handlePresetSelect = (presetKey: PresetKey) => {
    const preset = DATE_RANGE_PRESETS[presetKey];
    const range = preset.getRange();
    setDateRange(range);
    setSelectedPreset(presetKey);
    rangeProps?.onSelect?.(range);
  };

  // Showing the range to the day while the inputs below carry it to the minute would read as a
  // narrower window than the one in force, so the trigger picks up the time too.
  const rangeFormatStr = showTimeInput ? getRangeFormatWithTime(formatStr) : formatStr;

  const getDisplayText = () => {
    if (singleProps) {
      return date ? formatDate(date, formatStr) : placeholder;
    } else {
      if (!dateRange?.from) return placeholder;
      if (!dateRange.to) return `${formatDate(dateRange.from, rangeFormatStr)} - ...`;
      return `${formatDate(dateRange.from, rangeFormatStr)} - ${formatDate(dateRange.to, rangeFormatStr)}`;
    }
  };

  const hasValue = singleProps ? !!date : !!dateRange?.from;

  return (
    <Popover onOpenChange={setOpen} open={open}>
      <PopoverTrigger asChild>
        <Button
          className={input({
            className: twMerge(
              'hover:bg-inherit w-auto justify-between data-[state=open]:ring-1 data-[state=open]:ring-primary data-[state=open]:ring-inset',
              !hasValue ? datePickerPlaceholder() : undefined,
              className
            ),
          })}
          aria-label={ariaLabel}
          data-slot="date-picker-trigger"
          disabled={disabled}
          variant="secondary"
        >
          <Label className="cursor-pointer">{getDisplayText()}</Label>
          <FontAwesomeIcon className="ml-2 h-4 w-4 text-secondary" icon={faCalendar} />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-auto max-w-(--radix-popover-content-available-width) p-0"
        align="start"
        collisionPadding={12}
      >
        {singleProps ? (
          <Calendar
            autoFocus
            captionLayout="dropdown"
            defaultMonth={date}
            disabled={disabled || singleProps.disabledDays}
            endMonth={endMonth}
            mode="single"
            onSelect={handleSelectSingle}
            selected={date}
            startMonth={startMonth}
          />
        ) : (
          <FlexContainer direction="column">
            <Calendar
              autoFocus
              captionLayout="dropdown"
              disabled={disabled}
              mode="range"
              numberOfMonths={isMobile ? 1 : 2}
              onSelect={handleSelectRange}
              selected={dateRange}
            />
            {showTimeInput && (
              <FlexContainer className="border-t border-border-secondary p-3 w-full gap-3" items="center">
                <FlexContainer className="gap-2" items="center">
                  <Label htmlFor={`${timeInputId}-from`}>From time</Label>
                  <Input
                    className="w-auto"
                    disabled={disabled || !dateRange?.from}
                    id={`${timeInputId}-from`}
                    onChange={(event) => handleTimeChange('from', event.target.value)}
                    type="time"
                    value={toTimeValue(dateRange?.from)}
                  />
                </FlexContainer>
                <FlexContainer className="gap-2" items="center">
                  <Label htmlFor={`${timeInputId}-to`}>To time</Label>
                  <Input
                    className="w-auto"
                    disabled={disabled || !dateRange?.to}
                    id={`${timeInputId}-to`}
                    onChange={(event) => handleTimeChange('to', event.target.value)}
                    type="time"
                    value={toTimeValue(dateRange?.to)}
                  />
                </FlexContainer>
              </FlexContainer>
            )}
            {presets && presets.length > 0 && (
              <FlexContainer className="border-t border-border-secondary p-3 w-full" justify="end">
                <ToggleGroup
                  onValueChange={(value) => {
                    if (value) {
                      handlePresetSelect(value as PresetKey);
                    }
                  }}
                  size="sm"
                  type="single"
                  value={selectedPreset ?? ''}
                  variant="unbounded"
                >
                  {presets.map((presetKey) => {
                    const preset = DATE_RANGE_PRESETS[presetKey];
                    return (
                      <ToggleGroupItem key={presetKey} value={presetKey}>
                        {preset.label}
                      </ToggleGroupItem>
                    );
                  })}
                </ToggleGroup>
              </FlexContainer>
            )}
          </FlexContainer>
        )}
      </PopoverContent>
    </Popover>
  );
}

export { DatePicker, DATE_RANGE_PRESETS, type DatePickerProps, type PresetKey as DateRangePresetKey };
